import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { createApiUrl } from '../utils/apiConfig';
import { assetFileDisplayName } from '../utils/assetDisplayName';
import './Subscriptions.css';

const Subscriptions = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [plans, setPlans] = useState([]);
  const [showOrgCodeModal, setShowOrgCodeModal] = useState(false);
  const [orgCodeInput, setOrgCodeInput] = useState('');
  const [orgCodeError, setOrgCodeError] = useState('');

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(createApiUrl('/api/subscriptions'), {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        });
        setPlans(response.data.plans || []);
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };

    if (isOnline) {
      fetchPlans();
    }
  }, [isOnline]);

  useEffect(() => {
    if (user && user.isSchAdmin && !user.orgCode) {
      setShowOrgCodeModal(true);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      navigate('/login');
    }
  };

  const handleAddToBasket = async (planId, workshopDate = null) => {
    try {
      // Check if user is an org admin or school admin
      const isOrgAdmin = user?.isOrgAdmin;
      const isSchoolAdmin = user?.isSchAdmin;
      
      if (isOrgAdmin) {
        // For org admins, use the org-add-to-basket endpoint
        const response = await axios.post(createApiUrl(`/api/org-add-to-basket/${user._id}`), {
          planIds: [planId],
          quantity: 1,
          workshopDate: workshopDate
        }, {
          withCredentials: true
        });
        
        if (response.data.success) {
          navigate(response.data.redirectUrl || '/basket');
        } else {
          alert(response.data.message);
        }
      } else if (isSchoolAdmin) {
        // For school admins, use the sch-add-to-basket endpoint
        // School admins can only have ONE plan in their basket
        const response = await axios.post(createApiUrl(`/api/sch-add-to-basket/${planId}`), {
          quantity: 1,
          workshopDate: workshopDate
        }, {
          withCredentials: true
        });
        
        if (response.data.success) {
          navigate(response.data.redirectUrl || '/basket');
        } else {
          alert(response.data.message || 'You can only have one subscription plan in your basket. Please remove the existing plan before adding another.');
        }
      } else {
        // For regular users, use the regular add-to-basket endpoint
        const response = await axios.post(createApiUrl(`/api/add-to-basket/${planId}`), {
          workshopDate: workshopDate
        }, {
          withCredentials: true
        });
        
        if (response.data.success) {
          navigate(response.data.redirectUrl || '/basket');
        } else {
          alert(response.data.message);
        }
      }
    } catch (error) {
      console.error('Error adding to basket:', error);
      alert('Error adding to basket. Please try again.');
    }
  };

  const handleOrgCodeSubmit = async (e) => {
    e.preventDefault();
    setOrgCodeError('');
    try {
      const response = await axios.post(createApiUrl('/api/join-org'), { orgCode: orgCodeInput });
      if (response.data.success) {
        // Optionally update user context here
        navigate('/school-admin-dashboard');
      } else {
        setOrgCodeError(response.data.message || 'Invalid Org Code');
      }
    } catch (err) {
      setOrgCodeError('Error joining organization.');
    }
  };

  return (
    <div className="subscriptions-tab-content">
      {/* Org Code Modal */}
      {showOrgCodeModal && (
        <div className="modal-overlay" onClick={() => setShowOrgCodeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={() => setShowOrgCodeModal(false)}
              aria-label="Close modal"
            >
              ×
            </button>
            <h2>Join an Organization</h2>
            <form onSubmit={handleOrgCodeSubmit}>
              <input
                type="text"
                value={orgCodeInput}
                onChange={e => setOrgCodeInput(e.target.value)}
                placeholder="Enter Org Code"
                required
              />
              <button type="submit">Join</button>
              {orgCodeError && <div className="error">{orgCodeError}</div>}
            </form>
          </div>
        </div>
      )}
      <h1 className="centered-header">Available Plans</h1>
      {!isOnline && (
        <div className="connection-status">
          You are currently offline. Some features may be limited.
        </div>
      )}
      <div className="container">
        <div className="subscriptions-row">
          {plans
            .filter((plan) => plan.type !== 'workshop')
            .map((plan) => (
              <div key={plan._id} className="subscriptions-col-md-4">
                <div className="subscriptions-card overflow-auto">
                  <div className="subscriptions-card-body">
                    <div
                      className={`d-flex ${
                        plan.name === '30-day FREE trial'
                          ? 'justify-content-center'
                          : 'justify-content-between'
                      } mb-3`}
                    >
                      <button
                        className="subscriptions-btn btn btn-primary"
                        onClick={() => handleAddToBasket(plan._id)}
                        disabled={plan.assets.length === 0}
                      >
                        {plan.name === '30-day FREE trial'
                          ? 'Start your Free Trial Now'
                          : 'Buy Subscription'}
                      </button>
                      {plan.name !== '30-day FREE trial' && (
                        <button
                          onClick={handleLogout}
                          className="subscriptions-btn btn btn-danger logoutButton"
                        >
                          Log Out
                        </button>
                      )}
                    </div>

                    <h4 className="subscriptions-card-title">{plan.name}</h4>
                    {plan.name !== '30-day FREE trial' && (
                      <>
                        <p>
                          Duration:{' '}
                          {plan.duration < 30
                            ? `${plan.duration} days`
                            : plan.duration % 365 === 0
                            ? `${plan.duration / 365} year${
                                plan.duration / 365 > 1 ? 's' : ''
                              }`
                            : `${Math.ceil(plan.duration / 30)} months`}
                        </p>
                        <p>Price: {plan.price}</p>
                        {plan.description && <p>Description: {plan.description}</p>}
                      </>
                    )}
                    <br />
                    {plan.type !== 'workshop' && <h5>Lessons:</h5>}
                    <br />
                    <ul className="subscribed-asset">
                      {plan.assets?.map((asset) => (
                        <li key={asset._id}>
                          <h5>{asset.name}</h5>
                          <p>{asset.description}</p>
                          {asset.video?.thumbnail && (
                            <img
                              src={asset.video.thumbnail}
                              alt={`Thumbnail for ${asset.name}`}
                              className="asset-thumbnail thumbnail"
                              data-asset-id={asset._id}
                              style={{ cursor: 'default' }}
                            />
                          )}
                          {asset.file?.url && !asset.video?.thumbnail && (
                            <video
                              height="200"
                              width="280"
                              className="asset-video"
                              id={`video-${asset._id}`}
                              muted
                              loop
                              style={{ pointerEvents: 'none' }}
                            >
                              <source src={asset.file.url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          )}
                          {asset.documents?.map((doc, index) => (
                            doc.url && (
                              <div key={index}>
                                <span
                                  style={{ 
                                    color: '#FFB84D', 
                                    textDecoration: 'none',
                                    cursor: 'default'
                                  }}
                                >
                                  {assetFileDisplayName(doc)} (Preview only)
                                </span>
                                <br />
                              </div>
                            )
                          ))}
                          <hr />
                        </li>
                      ))}
                    </ul>
                    <div className="buy-btns">
                      <div className="button-container">
                        <button
                          className="subscriptions-btn btn btn-primary"
                          onClick={() => handleAddToBasket(plan._id)}
                          disabled={plan.assets.length === 0}
                        >
                          {plan.name === '30-day FREE trial'
                            ? 'Start your Free Trial Now'
                            : 'Buy Subscription'}
                        </button>
                        {plan.assets.length === 0 ? (
                          plan.name !== '30-day FREE trial' && (
                            <button className="subscriptions-btn btn btn-danger" disabled>
                              Log Out
                            </button>
                          )
                        ) : (
                          plan.name !== '30-day FREE trial' && (
                            <button
                              onClick={handleLogout}
                              className="subscriptions-btn btn btn-danger logoutButton"
                            >
                              Log Out
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <br />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
